// useBirthPlaceField — birth-place input state for the onboarding forms.
//
// status: 'empty' | 'typed' | 'resolving' | 'picked' | 'resolved' | 'unresolved' | 'check_failed'
//   picked       — the user chose a suggestion (its coordinates are used as-is)
//   resolved     — the user typed a place and /api/geo/resolve found it (the
//                  same geocode the save uses, so the save will accept it)
//   unresolved   — the backend looked and found nothing (retry allowed: blur
//                  again, e.g. after the user edits nothing but retries)
//   check_failed — the check itself failed (network); not a verdict on the place
// Typing always clears coordinates: a place edited after a pick must never be
// saved with the previously picked place's coordinates.
import { useCallback, useRef, useState } from 'react';
import { apiGet } from '../services/apiClient';
import { hasCoords } from '../utils/birthPlace';
import { trackEvent } from '../utils/analytics';

export const RESOLVED_STATUSES = ['picked', 'resolved'];
// States from which resolveTyped() may (re)ask the backend.
export const RESOLVABLE_STATUSES = ['typed', 'unresolved', 'check_failed'];

export const initialPlaceState = (place = '', latitude = null, longitude = null) => {
  const p = place || '';
  if (p.trim() && hasCoords(latitude, longitude)) {
    return { place: p, latitude: Number(latitude), longitude: Number(longitude), status: 'picked', matchedName: null };
  }
  return { place: p, latitude: null, longitude: null, status: p.trim() ? 'typed' : 'empty', matchedName: null };
};

export const typedPlaceState = (value) => ({
  place: value, latitude: null, longitude: null,
  status: (value || '').trim() ? 'typed' : 'empty', matchedName: null,
});

export const pickedPlaceState = (suggestion) => ({
  place: suggestion.name || suggestion.full_name || suggestion.display_name || '',
  latitude: Number(suggestion.latitude ?? suggestion.lat),
  longitude: Number(suggestion.longitude ?? suggestion.lon),
  status: hasCoords(suggestion.latitude ?? suggestion.lat, suggestion.longitude ?? suggestion.lon) ? 'picked' : 'typed',
  matchedName: null,
});

export const resolvedPlaceState = (place, geo) => (
  geo && geo.matched && hasCoords(geo.latitude, geo.longitude)
    ? { place, latitude: geo.latitude, longitude: geo.longitude, status: 'resolved', matchedName: geo.matched_name || null }
    : { place, latitude: null, longitude: null, status: 'unresolved', matchedName: null }
);

export const isPlaceReady = (state) => RESOLVED_STATUSES.includes(state.status) && hasCoords(state.latitude, state.longitude);

export default function useBirthPlaceField(initialPlace = '', initialLat = null, initialLon = null, surface = 'onboarding') {
  const [state, setStateRaw] = useState(() => initialPlaceState(initialPlace, initialLat, initialLon));
  // Callers invoke resolveTyped from a delayed blur handler, i.e. from a stale
  // render; the ref lets it see a suggestion picked in the meantime.
  const stateRef = useRef(state);
  const setState = useCallback((next) => {
    stateRef.current = typeof next === 'function' ? next(stateRef.current) : next;
    setStateRaw(stateRef.current);
  }, []);
  const seq = useRef(0);

  const onType = useCallback((value) => {
    seq.current += 1; // drop any in-flight resolve for the old text
    setState(typedPlaceState(value));
  }, [setState]);

  const onPick = useCallback((suggestion) => {
    seq.current += 1;
    setState(pickedPlaceState(suggestion));
  }, [setState]);

  // Ask the backend to resolve a typed (not picked) place. Call on blur, on
  // mount for a prefilled place, or from the hint's retry.
  const resolveTyped = useCallback(async () => {
    const current = stateRef.current;
    const place = (current.place || '').trim();
    if (!RESOLVABLE_STATUSES.includes(current.status) || !place) return;
    const mine = ++seq.current;
    setState((s) => ({ ...s, status: 'resolving' }));
    let geo = null;
    let networkFailed = false;
    try {
      const res = await apiGet(`/api/geo/resolve?q=${encodeURIComponent(place)}`);
      geo = res.data;
    } catch {
      networkFailed = true;
    }
    if (seq.current !== mine) return;
    if (networkFailed) {
      // Not a verdict on the place — no birth_place_unresolved event.
      setState({ ...current, latitude: null, longitude: null, status: 'check_failed', matchedName: null });
      return;
    }
    const next = resolvedPlaceState(current.place, geo);
    trackEvent(next.status === 'resolved' ? 'birth_place_resolved_typed' : 'birth_place_unresolved',
      { surface, reason: 'typed' });
    setState(next);
  }, [setState, surface]);

  // A save came back 503 place_check_busy: "Couldn't check the place — tap to retry".
  const markCheckFailed = useCallback(() => {
    setState((s) => ({ ...s, latitude: null, longitude: null, status: 'check_failed', matchedName: null }));
  }, [setState]);

  // A save came back 422 place_unresolved.
  const markUnresolved = useCallback(() => {
    trackEvent('birth_place_unresolved', { surface, reason: 'save_422' });
    setState((s) => ({ ...s, latitude: null, longitude: null, status: 'unresolved', matchedName: null }));
  }, [setState, surface]);

  return { ...state, ready: isPlaceReady(state), onType, onPick, resolveTyped, markUnresolved, markCheckFailed };
}
